import { useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UploadCloud, FileSpreadsheet, Loader2, Mail, Sparkles, CheckCircle2, AlertTriangle,
} from "lucide-react";

export default function ImportPage() {
  const { user } = useAuth();
  const { executives } = useFilters();
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [kind, setKind] = useState("tieups");
  const [selectedSheet, setSelectedSheet] = useState("");
  const [execId, setExecId] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestStatus, setDigestStatus] = useState(null);

  const onFile = async (f) => {
    setFile(f);
    setSheets([]);
    setSelectedSheet("");
    setResult(null);
    if (!f) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSheets(r.data.sheets || []);
      if (r.data.sheets?.length === 1) setSelectedSheet(r.data.sheets[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read workbook");
    } finally { setPreviewing(false); }
  };

  const runImport = async () => {
    if (!file || !selectedSheet) { toast.error("Choose file and sheet"); return; }
    if (kind === "daily_logs" && !execId) { toast.error("Select executive for daily logs"); return; }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sheet_name", selectedSheet);
      if (kind === "daily_logs") fd.append("executive_id", execId);
      const endpoint = kind === "tieups" ? "/import/tieups" : "/import/daily-logs";
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} row${r.data.imported !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    setDigestStatus(null);
    try {
      const r = await api.post("/digest/send-now");
      setDigestStatus({ ok: true, to: r.data.sent_to });
      toast.success(`Digest sent to ${r.data.sent_to}`);
    } catch (e) {
      setDigestStatus({ ok: false, error: e?.response?.data?.detail || "Failed" });
      toast.error("Digest failed");
    } finally { setSendingDigest(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="import-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Migration & Automation</div>
        <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
          Import &amp; Digest
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Bring in pre-February Excel data and control the Monday founder digest.
        </p>
      </div>

      {/* Excel Import */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-800 flex items-center gap-2">
          <FileSpreadsheet size={17} className="text-[#C9A24B]" />
          <div>
            <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Bulk Excel Import</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Upload the old <span className="mono">.xlsx</span> workbook, pick a sheet, and import as tie-ups or an executive&apos;s daily logs.
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">1. Choose workbook</label>
            <div className="mt-2 flex items-center gap-3">
              <input
                data-testid="import-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => onFile(e.target.files?.[0])}
                className="text-sm text-slate-200 file:bg-[#C9A24B] file:text-[#0F172A] file:font-semibold file:rounded-lg file:border-0 file:px-3 file:py-2 file:mr-3 file:cursor-pointer"
              />
              {previewing && <Loader2 size={14} className="animate-spin text-[#C9A24B]" />}
            </div>
          </div>

          {sheets.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">2. Data type</label>
                  <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger data-testid="import-kind" className="bg-slate-900 border-slate-700 mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      <SelectItem value="tieups">Partner Tie-Ups Log</SelectItem>
                      <SelectItem value="daily_logs">Daily Activity Log</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">3. Sheet</label>
                  <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                    <SelectTrigger data-testid="import-sheet" className="bg-slate-900 border-slate-700 mt-2"><SelectValue placeholder="Choose sheet" /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      {sheets.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {kind === "daily_logs" && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">4. Executive</label>
                    <Select value={execId} onValueChange={setExecId}>
                      <SelectTrigger data-testid="import-exec" className="bg-slate-900 border-slate-700 mt-2"><SelectValue placeholder="Whose log is this?" /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        {executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <button
                data-testid="btn-run-import"
                onClick={runImport}
                disabled={importing || !selectedSheet || (kind === "daily_logs" && !execId)}
                className="bg-[#C9A24B] hover:bg-[#b08d40] disabled:opacity-60 text-[#0F172A] font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2"
              >
                {importing ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
                Import Rows
              </button>

              {result && (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid="import-result">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                    <CheckCircle2 size={16} /> {result.imported} row(s) imported
                  </div>
                  {result.error_count > 0 && (
                    <div className="mt-2">
                      <div className="text-rose-400 text-xs font-semibold flex items-center gap-1"><AlertTriangle size={12} /> {result.error_count} row(s) skipped</div>
                      <ul className="text-xs text-slate-400 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-xs text-slate-400">
                <div className="font-semibold text-slate-300 mb-1">Column matching (auto)</div>
                • Tie-Ups: <span className="mono">Date, Executive Name, Tie-Up Type, Contact Person, Organisation Name, Contact Number, Address/Area, Agreement Signed, First Referral Date, Follow-Up Date, Remarks</span><br />
                • Daily Logs: <span className="mono">Date, Revenue Generated, New Partners, Partner Visits, Total Active Partners, Referrals Generated / Converted, CRM Entries, Commission Disputes, Report Submitted, Event Organized, Partners at Event, Remarks</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Founder Digest */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-800 flex items-center gap-2">
          <Mail size={17} className="text-[#C9A24B]" />
          <div>
            <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Founder Weekly Digest</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Every Monday at 8:00 AM IST, an HTML digest is emailed to the founder with revenue, top/bottom performer, alerts, and an AI recap.
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Recipient</div>
              <div className="text-white text-sm mt-1 truncate">{user?.email || "—"} <span className="text-[10px] text-[#C9A24B] uppercase ml-1">Founder</span></div>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Schedule</div>
              <div className="text-white text-sm mt-1">Mon 08:00 IST</div>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1">
                <Sparkles size={11} className="text-[#C9A24B]" /> AI Recap
              </div>
              <div className="text-white text-sm mt-1">Claude Sonnet 4.6</div>
            </div>
          </div>

          <button
            data-testid="btn-send-digest-now"
            onClick={sendDigestNow}
            disabled={sendingDigest}
            className="bg-[#C9A24B] hover:bg-[#b08d40] disabled:opacity-60 text-[#0F172A] font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2"
          >
            {sendingDigest ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            Send Test Digest Now
          </button>

          {digestStatus && (
            <div className={`rounded-lg p-3 text-sm ${digestStatus.ok ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border border-rose-500/30 text-rose-300"}`}>
              {digestStatus.ok ? `✓ Digest sent to ${digestStatus.to}` : `✗ ${digestStatus.error}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
